import { Box, ButtonProps, IconProps, SpaceBetween } from "@amzn/awsui-components-react";
import Button from "@amzn/uxm-aws-ui-components-react/lib/src/polaris/button";
import CustomModal, { CustomModalRef } from "../CustomModal/CustomModal";
import React, { useRef } from "react";
import { CONFIRM_MODAL } from "helpers";

/**
 * Represent the props for the ConfirmActionButton component
 *
 * @interface
 *
 * Button Configuration:
 * @param {ButtonProps.IconAlign} iconAlign - Alignment of the icon relative to button text ('left' or 'right')
 * @param {IconProps.Name} iconName - Name of the icon to display on the button
 * @param {() => void} onClick - Click handler function for the button
 * @param {string} content - Text content to display on the button
 * @param {ButtonProps.Variant} variant - Visual variant of the button
 * @param {boolean} disabled - Represents if the button is disabled
 * @param {string} disabledReason - The text shown when the button is disabled
 * @param {string} touchpoint = The UxM component touchpoint
 *
 * Modal Configuration:
 * @param {React.ReactNod} modalTitle - Title text/content for the confirmation modal
 * @param {React.ReactNod} modalCancel - Text/content for the modal's cancel button
 * @param {React.ReactNod} modalConfirm - Text/content for the modal's confirm button
 * @param {React.ReactNod} modalDescription - Description text/content shown in the modal body
 */
export interface ConfirmActionButtonProps {
    /** Button Configuration */
    iconAlign?: ButtonProps.IconAlign;
    iconName: IconProps.Name;
    onClick: () => void;
    content: string;
    variant?: ButtonProps.Variant;
    disabled?: boolean;
    disabledReason?: string;
    touchpoint: string;

    /** Modal Configuration */
    modalTitle: React.ReactNode;
    modalCancel: React.ReactNode;
    modalConfirm: React.ReactNode;
    modalDescription: React.ReactNode;
}

const ConfirmActionButton = ({
    iconAlign = "left",
    iconName,
    content,
    variant,
    disabled,
    disabledReason,
    onClick,
    modalTitle,
    modalCancel,
    modalConfirm,
    modalDescription,
    touchpoint,
}: ConfirmActionButtonProps) => {
    const modalRef = useRef<CustomModalRef>(null);

    const setModalToInvisible = () => {
        if (modalRef.current) {
            modalRef.current.close();
        }
    };

    const onClickAction = () => {
        onClick();
        setModalToInvisible();
    };

    const actionButtons = (
        <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
                <Button
                    onClick={setModalToInvisible}
                    data-testid={CONFIRM_MODAL.CANCEL}
                    touchpoint={`${touchpoint}-cancelled`}
                >
                    {modalCancel}
                </Button>
                <Button
                    onClick={onClickAction}
                    variant="primary"
                    data-testid={CONFIRM_MODAL.ACCEPT}
                    touchpoint={`${touchpoint}-accepted`}
                >
                    {modalConfirm}
                </Button>
            </SpaceBetween>
        </Box>
    );

    return (
        <CustomModal
            iconAlign={iconAlign}
            iconName={iconName}
            variant={variant}
            buttonContent={content}
            disabled={disabled}
            disabledReason={disabledReason}
            modalFooter={actionButtons}
            modalHeader={modalTitle}
            modalContent={modalDescription}
            ref={modalRef}
        />
    );
};

export default ConfirmActionButton;
